import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { FaWallet } from 'react-icons/fa';

interface SoldeRemiseModalProps {
  show: boolean;
  onClose: () => void;
  soldeRemise: number;
  maxAmount: number;
  onApply: (amount: number) => void;
  currentAmount: number;
}

const SoldeRemiseModal: React.FC<SoldeRemiseModalProps> = ({
  show,
  onClose,
  soldeRemise,
  maxAmount,
  onApply,
  currentAmount
}) => {
  const [amountToUse, setAmountToUse] = useState<number>(0);

  useEffect(() => {
    if (show) {
      // Initialiser avec le montant maximum disponible (min entre solde et montant restant)
      const availableAmount = Math.min(soldeRemise, maxAmount);
      setAmountToUse(availableAmount);
    }
  }, [show, soldeRemise, maxAmount]);

  const handleApply = () => {
    if (amountToUse > 0 && amountToUse <= soldeRemise && amountToUse <= maxAmount) {
      onApply(amountToUse);
      onClose();
    }
  };

  const handleReset = () => {
    setAmountToUse(0);
    onApply(0);
    onClose();
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number.parseFloat(e.target.value);
    setAmountToUse(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number.parseFloat(e.target.value) || 0;
    const capped = Math.min(value, Math.min(soldeRemise, maxAmount));
    const positive = Math.max(capped, 0);
    setAmountToUse(positive);
  };

  const maxUsable = Math.min(soldeRemise, maxAmount);
  const isCurrentlyApplied = currentAmount > 0;

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton className="border-0" style={{ backgroundColor: '#4BB0C0', color: 'white' }}>
        <Modal.Title className="fw-bold">
          <FaWallet className="me-2" />
          Utiliser mon solde remise
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-4">
        {/* Info solde disponible */}
        <Alert variant="info" className="border-0 mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Solde remise disponible:</strong>
              <div style={{ fontSize: '1.3rem', color: '#4BB0C0', fontWeight: 'bold', marginTop: '4px' }}>
                {soldeRemise.toFixed(2)} €
              </div>
            </div>
            <FaWallet style={{ fontSize: '32px', color: '#4BB0C0', opacity: 0.6 }} />
          </div>
        </Alert>

        {/* Info montant maximum utilisable */}
        {maxUsable < soldeRemise && (
          <Alert variant="warning" className="border-0 mb-4">
            <strong>Montant maximum utilisable:</strong> {maxUsable.toFixed(2)} €
            <div className="small mt-2">
              (Vous ne pouvez utiliser que jusqu'à {maxUsable.toFixed(2)} € sur cette annonce)
            </div>
          </Alert>
        )}

        {/* Montant actuellement appliqué */}
        {isCurrentlyApplied && (
          <Alert variant="success" className="border-0 mb-4">
            <strong>Montant actuellement appliqué:</strong> {currentAmount.toFixed(2)} €
          </Alert>
        )}

        {/* Slider */}
        <div className="mb-4">
          <Form.Group>
            <Form.Label className="fw-bold mb-3">Montant à utiliser</Form.Label>
            <Form.Range
              min="0"
              max={maxUsable}
              step="0.01"
              value={amountToUse}
              onChange={handleSliderChange}
              className="mb-3"
            />
            <div className="d-flex justify-content-between text-muted small">
              <span>0 €</span>
              <span>{maxUsable.toFixed(2)} €</span>
            </div>
          </Form.Group>
        </div>

        {/* Input direct */}
        <div className="mb-4">
          <Form.Group>
            <Form.Label className="fw-bold">Montant (€)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              max={maxUsable}
              step="0.01"
              value={amountToUse.toFixed(2)}
              onChange={handleInputChange}
              className="border-0 shadow-sm"
              style={{ backgroundColor: '#f8f9fa', fontSize: '1.1rem', fontWeight: 'bold', color: '#2A919D' }}
            />
          </Form.Group>
        </div>

        {/* Résumé */}
        <div className="bg-light p-3 rounded border border-secondary mb-4">
          <Row className="mb-2">
            <Col xs={8}>
              <span>Montant utilisé:</span>
            </Col>
            <Col xs={4} className="text-end">
              <span className="fw-bold" style={{ color: '#FF8C00', fontSize: '1.1rem' }}>
                -{amountToUse.toFixed(2)} €
              </span>
            </Col>
          </Row>
          <Row>
            <Col xs={8}>
              <span>Solde restant:</span>
            </Col>
            <Col xs={4} className="text-end">
              <span className="fw-bold" style={{ color: '#4BB0C0', fontSize: '1.1rem' }}>
                {(soldeRemise - amountToUse).toFixed(2)} €
              </span>
            </Col>
          </Row>
        </div>

        {amountToUse === 0 && (
          <Alert variant="warning" className="border-0">
            Aucun montant sélectionné. Cliquez sur "Réinitialiser" pour ne pas utiliser votre solde.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0">
        <Button
          variant="secondary"
          onClick={handleReset}
          className="px-4"
        >
          Réinitialiser
        </Button>
        <Button
          onClick={onClose}
          className="px-4"
          style={{ backgroundColor: '#f0f0f0', color: '#2A919D', border: 'none' }}
        >
          Fermer
        </Button>
        <Button
          onClick={handleApply}
          disabled={amountToUse === 0}
          className="px-4"
          style={{ backgroundColor: '#4BB0C0', border: 'none', color: 'white' }}
        >
          Utiliser {amountToUse.toFixed(2)} €
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SoldeRemiseModal;
